import {
  ValidationKindTypes,
  ValidationKindToType,
  ValidationKind
} from '@publishing-engine/domain/types/article';
import { FieldError as IFieldError } from 'react-hook-form';
import {
  pluralizeFieldName,
  pluralizeString,
  toSentenceCase
} from 'src/utils/string';

type ValidationErrorMessages = {
  [K in ValidationKindTypes]: (
    fieldName: string,
    validation: ValidationKindToType[K],
    error: IFieldError
  ) => string;
};

export const validationErrorMessages: ValidationErrorMessages = {
  string: (fieldName, validation, error) => {
    if (error.type === 'required' && validation.required) {
      return `Please enter a ${fieldName}`;
    }
    if (error.type === 'maxLength' && validation.maxLength !== undefined) {
      return `Maximum ${validation.maxLength} characters reached. Please shorten your text`;
    }
    if (error.type === 'minLength' && validation.minLength !== undefined) {
      return `${fieldName} must be at least ${
        validation.minLength
      } ${pluralizeString('character', validation.minLength)}`;
    }
    if (error.type === 'maxWords' && validation.maxWords !== undefined) {
      return `Maximum ${validation.maxWords} ${pluralizeString(
        'word',
        validation.maxWords
      )} reached. Please shorten your text`;
    }
    if (error.type === 'minWords' && validation.minWords !== undefined) {
      return `${fieldName} must be at least ${
        validation.minWords
      } ${pluralizeString('word', validation.minWords)}`;
    }
    if (
      error.type === 'richTextMaxLength' &&
      validation.maxLength !== undefined
    ) {
      return `Maximum ${validation.maxLength} characters reached. Please shorten your text`;
    }
    if (
      error.type === 'richTextMinLength' &&
      validation.minLength !== undefined
    ) {
      return `${fieldName} must be at least ${
        validation.minLength
      } ${pluralizeString('character', validation.minLength)}`;
    }
    if (error.type === 'pattern') {
      if (fieldName.toLowerCase().includes('doi')) {
        return `Please enter a valid ${fieldName} (eg., 10.1038/example)`;
      }
    }
    if (
      error.type === 'custom' ||
      error.type === 'manual' ||
      error.type === 'validate'
    ) {
      return error.message || `Please enter a valid ${fieldName}`;
    }
    return `Please enter a valid ${fieldName}`;
  },
  nullString: (fieldName, validation, error) => {
    if (error.type === 'required' && validation.required) {
      return `Please enter a ${fieldName}`;
    }
    if (error.type === 'maxLength' && validation.maxLength !== undefined) {
      return `Maximum ${validation.maxLength} characters reached. Please shorten your text`;
    }
    return `Please enter a valid ${fieldName}`;
  },
  funding: (fieldName, validation, error) => {
    if (error.type === 'required' && validation.required) {
      return `Please provide ${fieldName} information`;
    }
    return `Please provide valid ${fieldName} information`;
  },
  fundingGeneric: (fieldName, validation, error) => {
    if (error.type === 'required' && validation.required) {
      return `Please provide ${fieldName} information`;
    }
    return `Please provide valid ${fieldName} information`;
  },
  bool: (fieldName, validation, error) => {
    if (fieldName?.trim() === '' && validation.required) {
      return `Confirmation is required`;
    }
    if (error.type === 'required' && validation.required) {
      return `Please select a ${fieldName} option`;
    }

    return `Please select a valid ${fieldName} option`;
  },
  file: (fieldName, validation, error) => {
    if (error.type === 'required' && validation.required) {
      return `Please upload a ${fieldName} file`;
    }
    const formats = Object.values(validation.allowedFileFormats)
      .flat()
      .join(', ');

    return `Please upload a valid ${fieldName} file (max ${validation.maxSizeMB}MB, allowed formats: ${formats})`;
  },
  fileArray: (fieldName, validation, error) => {
    if (error.type === 'required' && validation.required) {
      return `Please upload ${fieldName} files`;
    }
    const formats = Object.values(validation.allowedFileFormats)
      .flat()
      .join(', ');
    const minItemsMsg = validation.minItems
      ? ` at least ${validation.minItems} files`
      : '';
    return `Please upload${minItemsMsg} valid ${fieldName} files (max ${validation.maxSizeMB}MB each, allowed formats: ${formats})`;
  },
  figuresAndTables: (fieldName, validation, error) => {
    if (error.type === 'required' && validation.required) {
      return `Please provide ${fieldName} with source files and permission evidence`;
    }
    return `Please provide valid ${fieldName} with source files and permission evidence`;
  },
  options: (fieldName, validation, error) => {
    if (error.type === 'required' && validation.required) {
      return `Please select a ${fieldName} option`;
    }
    return `Please select a valid ${fieldName} option`;
  },
  authors: (fieldName, validation, error) => {
    if (error.type === 'required' && validation.required) {
      return `Please add ${fieldName}`;
    }
    if (error.type === 'minItems' && validation.minItems) {
      return `Please add at least ${validation.minItems} ${fieldName}`;
    }
    return `Please add valid ${fieldName}`;
  },
  author: (fieldName, validation, error) => {
    if (error.type === 'required' && validation.required) {
      return `Please provide ${fieldName} information`;
    }
    if (error.type === 'orcidRequired' && validation.orcidRequired) {
      return `Please provide a valid ORCID for ${fieldName}`;
    }
    return `Please provide valid ${fieldName} information`;
  },
  authorType: (fieldName, validation, error) => {
    if (error.type === 'required' && validation.required) {
      return `Please provide ${fieldName} information`;
    }
    return `Please provide valid ${fieldName} information`;
  },
  stringArray: (fieldName, validation, error) => {
    if (error.type === 'required' && validation.required) {
      return `Please add ${fieldName}`;
    }
    if (error.type === 'minCountOfKeywords' && validation.minCountOfKeywords) {
      return `Please add at least ${validation.minCountOfKeywords} ${fieldName}`;
    }
    if (error.type === 'maxCountOfKeywords' && validation.maxCountOfKeywords) {
      return `Please add up to ${validation.maxCountOfKeywords} ${fieldName}`;
    }
    if (error.type === 'maxItems' && validation.maxCountOfKeywords) {
      return `Only ${validation.maxCountOfKeywords} ${fieldName} are allowed`;
    }
    return `Please add valid ${fieldName}`;
  },
  publishingPreference: (fieldName, validation, error) => {
    if (error.type === 'required' && validation.required) {
      return `Please select a ${fieldName}`;
    }
    return `Please select a valid ${fieldName}`;
  },
  dependantStringField: (fieldName, validation, error) => {
    // react-hook-form sometimes nests the real error inside error.value (e.g. {root:{}, value:{type, message}})
    const resolvedError = (error as IFieldError & { value?: IFieldError })
      ?.value?.type
      ? (error as IFieldError & { value: IFieldError }).value
      : error;
    if (resolvedError.type === 'required' && validation.required) {
      return `Please enter a ${fieldName}`;
    }
    if (resolvedError.type === 'maxLength' && validation.maxLength) {
      return `Maximum ${validation.maxLength} characters reached. Please shorten your text`;
    }
    if (resolvedError.type === 'pattern' && validation.pattern) {
      return `Please enter a valid ${fieldName.toLowerCase()}`;
    }
    return `${fieldName} is required`;
  },
  reviewers: (fieldName, validation, error) => {
    if (error.type === 'required' && validation.required) {
      return `Please add ${fieldName}`;
    }
    if (error.type === 'minItems' && validation.minItems) {
      return `Please add at least ${validation.minItems} ${fieldName}`;
    }
    return `Please add valid ${fieldName}`;
  },
  reviewer: (fieldName, validation, error) => {
    if (error.type === 'required' && validation.required) {
      return `Please provide ${fieldName} information`;
    }
    return `Please provide valid ${fieldName} information`;
  },
  reviewsForAuthor: (fieldName, validation, error) => {
    if (error.type === 'required' && validation.required) {
      return `Please add ${fieldName}`;
    }
    if (error.type === 'minItems' && validation.minItems) {
      return `Please add at least ${validation.minItems} ${fieldName}`;
    }
    return `Please add valid ${fieldName}`;
  },
  reviewForAuthor: (fieldName, validation, error) => {
    if (error.type === 'required' && validation.required) {
      return `Please provide ${fieldName} information`;
    }
    return `Please provide valid ${fieldName} information`;
  },
  number: (fieldName, validation, error) => {
    if (error.type === 'required' && validation.required) {
      return `Please enter a ${fieldName}`;
    }

    if (error.type === 'minimum' && validation.min !== undefined) {
      return `${fieldName} must be at least ${validation.min}`;
    }
    if (error.type === 'maximum' && validation.max !== undefined) {
      return `${fieldName} must be at most ${validation.max}`;
    }
    return `Please enter a valid ${fieldName}`;
  },
  integer: (fieldName, validation, error) => {
    if (error.type === 'required' && validation.required) {
      return `Please enter a ${fieldName}`;
    }

    if (error.type === 'minimum' && validation.min !== undefined) {
      return `${fieldName} must be at least ${validation.min}`;
    }
    if (error.type === 'maximum' && validation.max !== undefined) {
      return `A maximum of  ${validation.max}  ${
        fieldName.toLocaleLowerCase().includes('number of')
          ? fieldName.toLocaleLowerCase().replace('number of', '').trim()
          : fieldName.toLocaleLowerCase()
      } is allowed`;
    }
    if (error.type === 'type') {
      if (
        fieldName.toLocaleLowerCase().includes('number of tables') ||
        fieldName.toLocaleLowerCase().includes('number of figures') ||
        fieldName.toLocaleLowerCase().includes('number of colour figures')
      ) {
        return `${toSentenceCase(fieldName)} is required`;
      }
      if (fieldName.toLocaleLowerCase().includes('number of words')) {
        return 'Word count is required';
      }
    }

    return `Please enter a valid ${fieldName}`;
  },
  date: (fieldName, validation, error) => {
    if (
      error.type === 'invalid_date' ||
      (error.type === 'required' && validation.required)
    ) {
      return `Please enter a ${fieldName}`;
    }

    return error.message || `Please enter a valid ${fieldName}`;
  },
  dataAvailabilityPreference: (fieldName, validation, error) => {
    if (error.type === 'required' && validation.required) {
      return `Please select a ${fieldName}`;
    }
    return `Please select a valid ${fieldName}`;
  },
  phoneNumber: (fieldName, validation, error) => {
    if (error.type === 'required' && validation.required) {
      return `Please enter a ${fieldName}`;
    }
    if (error.type === 'pattern') {
      return `Please enter a valid ${fieldName}`;
    }
    if (error.type === 'maxLength' && validation.maxLength !== undefined) {
      return `Maximum ${validation.maxLength} characters reached. Please shorten your text`;
    }
    if (error.type === 'minLength' && validation.minLength !== undefined) {
      return `${fieldName} must be at least ${
        validation.minLength
      } ${pluralizeString('character', validation.minLength)}`;
    }
    if (error.type === 'custom' || error.type === 'validate') {
      return error.message || `Please enter a valid ${fieldName}`;
    }
    return `Please enter a valid ${fieldName}`;
  },
  password: (fieldName, validation, error) => {
    if (error.type === 'required' && validation.required) {
      return `Please enter a ${fieldName}`;
    }
    if (error.type === 'pattern') {
      return `Please enter a valid ${fieldName}`;
    }
    if (error.type === 'maxLength' && validation.maxLength !== undefined) {
      return `Maximum ${validation.maxLength} characters reached. Please shorten your text`;
    }
    if (error.type === 'minLength' && validation.minLength !== undefined) {
      return `${fieldName} must be at least ${
        validation.minLength
      } ${pluralizeString('character', validation.minLength)}`;
    }
    if (error.type === 'custom' || error.type === 'validate') {
      return error.message || `Please enter a valid ${fieldName}`;
    }
    return `Please enter a valid ${fieldName}`;
  },
  customArray: (fieldName, validation, error) => {
    if (error.type === 'required' && validation.required) {
      return `Please add ${fieldName}`;
    }
    if (error.type === 'minItems') {
      // The schema gives a required array `minItems: 1` even when the config
      // sets no explicit minimum, and AJV then reports `minItems` rather than
      // `required`, so fall back the same way instead of a generic message.
      const minItems = validation.minItems ?? (validation.required ? 1 : 0);
      if (minItems) {
        return `Please add at least ${minItems} ${pluralizeFieldName(
          fieldName,
          minItems
        )}`;
      }
    }
    if (error.type === 'maxItems' && validation.maxItems) {
      return `You can add at most ${validation.maxItems} ${pluralizeFieldName(
        fieldName,
        validation.maxItems
      )}`;
    }
    if (error.type === 'minLength') {
      if (fieldName.toLowerCase().includes('caption')) {
        return `Please add valid caption`;
      }
      return `Please add valid ${fieldName}`;
    }
    if (error.type === 'pattern') {
      if (fieldName.toLowerCase().includes('caption')) {
        if (fieldName.toLowerCase().includes('tables')) {
          return `Captions cannot start with “Table” followed by a numeric identifier`;
        } else {
          return `Captions cannot start with “Figure” followed by a numeric identifier`;
        }
      }
      if (fieldName.toLowerCase().includes('alt-text')) {
        return `Alt-Text cannot start with “Image of” or contain line breaks or special characters (” ”, &, [ ], =)`;
      }
    }
    if (error.type === 'type') {
      if (fieldName.toLowerCase().includes('file')) {
        return `Please upload a valid file for your figure`;
      }
    }
    if (
      error.type === 'custom' ||
      error.type === 'manual' ||
      error.type === 'validate'
    ) {
      return error.message || `Please enter a valid ${fieldName}`;
    }
    return `Please add valid ${fieldName}`;
  },
  openAccessOnly: (fieldName, validation, error) => {
    if (error.type === 'required' && validation.required) {
      return `Please select a ${fieldName}`;
    }
    return `Please select a valid ${fieldName}`;
  },
  dependantCheckboxArray: (fieldName, validation, error) => {
    if (error.type === 'required' && validation.required) {
      return `Please select a ${fieldName}`;
    }
    return `Please select a valid ${fieldName}`;
  },
  dependantCheckboxArrayWithText: (fieldName, validation, error) => {
    if (error.type === 'required' && validation.required) {
      return `Please select the ${fieldName} used`;
    }
    if (error.type === 'pattern') {
      return `Please enter a description`;
    }
    if (error.type === 'minLength') {
      return `Please enter the valid ${fieldName} used`;
    }
    return `Please select the valid ${fieldName} used`;
  },
  objectArray: (fieldName, validation, error) => {
    if (error.type === 'required' && validation.required) {
      return `Please add ${fieldName}`;
    }
    if (error.type === 'minItems' && validation.minItems) {
      return `Please add at least ${validation.minItems} ${fieldName}`;
    }
    if (error.type === 'maxItems' && validation.maxItems) {
      return `You can add at most ${validation.maxItems} ${fieldName}`;
    }
    if (error.type === 'pattern') {
      if (fieldName.toLowerCase().includes('caption')) {
        if (fieldName.toLowerCase().includes('tables')) {
          return `Captions cannot start with “Table” followed by a numeric identifier`;
        } else {
          return `Captions cannot start with “Figure” followed by a numeric identifier`;
        }
      }
      if (fieldName.toLowerCase().includes('alt-text')) {
        return `Alt-Text cannot start with “Image of” or contain line breaks or special characters (” ”, &, [ ], =)`;
      }
    }
    if (error.type === 'type') {
      if (fieldName.toLowerCase().includes('file')) {
        return `Please upload a valid file for your figure`;
      }
    }
    if (
      error.type === 'custom' ||
      error.type === 'manual' ||
      error.type === 'validate'
    ) {
      return error.message || `Please enter a valid ${fieldName}`;
    }
    return `Please add valid ${fieldName}`;
  },
  targetJournals: (fieldName, validation, error) => {
    if (error.type === 'required' && validation.required) {
      return `Please select ${fieldName}`;
    }
    if (error.type === 'minItems' && validation.minItems) {
      return `Please select at least ${validation.minItems} ${fieldName}`;
    }
    return `Please select valid ${fieldName}`;
  }
};

export function getValidationTypedMessage<K extends ValidationKindTypes>(
  label: string,
  kind: K,
  validations: ValidationKind,
  error: IFieldError
): string {
  const messageFunction = validationErrorMessages[kind];
  const narrowed = validations as ValidationKindToType[K];
  return messageFunction(label, narrowed, error);
}
